import { useRef } from 'react'

import { gsap, registerGsap, prefersReducedMotion } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'
import { Reveal } from '@/components/animation/reveal'

const SERVICE_ICONS = [
    '/images/monitor-icon.svg',
    '/images/smartphone-icon.svg',
    '/images/anchor-icon.svg'
]

const SERVICES = [
    { words: ['Visual', 'identities'], no: '1' },
    { words: ['Product', 'Design'], no: '2' },
    { words: ['Modulify', 'Web Dev'], no: '3' }
]

export function Services() {
    const sectionRef = useRef<HTMLDivElement>(null)

    // a-45 "Display Service Icons On Scroll": the icon column tracks the scroll
    // position through the three services (yPercent -67 -> -6 -> 4 -> 67) and the
    // icons + titles dim to 0.4 / 0.5 during the transitions between them.
    useIsomorphicLayoutEffect(() => {
        const el = sectionRef.current
        if (!el || prefersReducedMotion()) return
        registerGsap()

        const ctx = gsap.context(() => {
            const icons = el.querySelector('.service-icons')
            const titles = el.querySelectorAll('.service-title')
            if (!icons) return

            gsap.set(icons, { yPercent: -67, opacity: 1 })
            gsap.set(titles, { opacity: 1 })

            // Trigger window spans roughly "service 1 centred" (section top at 15%)
            // to "service 3 centred" (section centre at the top). So icon 1 holds
            // while "Visual identities" rises to the vertical middle, then slides to
            // icon 2 as "Product Design" reaches the middle, then to icon 3.
            const tl = gsap.timeline({
                defaults: { ease: 'none' },
                scrollTrigger: {
                    trigger: el,
                    start: 'top 15%',
                    end: 'center top',
                    scrub: true
                }
            })

            // icon 1 -> 2 over the first half, icon 2 -> 3 over the second.
            tl.to(icons, { yPercent: 0, duration: 0.56 }, 0)
            tl.to(icons, { yPercent: 67, duration: 0.44 }, 0.56)

            // Icons + titles are full at each rest (0, ~0.56, 1) and dim mid-slide
            // to mask the movement. Gaps hold the value while scrubbing.
            tl.to(icons, { opacity: 0.4, duration: 0.06 }, 0.12)
            tl.to(icons, { opacity: 1, duration: 0.06 }, 0.5)
            tl.to(icons, { opacity: 0.4, duration: 0.06 }, 0.66)
            tl.to(icons, { opacity: 1, duration: 0.06 }, 0.9)

            tl.to(titles, { opacity: 0.5, duration: 0.06 }, 0.12)
            tl.to(titles, { opacity: 1, duration: 0.06 }, 0.5)
            tl.to(titles, { opacity: 0.5, duration: 0.06 }, 0.66)
            tl.to(titles, { opacity: 1, duration: 0.06 }, 0.9)
        }, sectionRef)

        return () => ctx.revert()
    }, [])

    return (
        <div ref={sectionRef} className='section services'>
            <div className='container no-max-width'>
                <div className='w-layout-grid grid-services'>
                    <div className='service-icons'>
                        {SERVICE_ICONS.map((icon) => (
                            <div className='service-icon-content' key={icon}>
                                <img src={icon} alt='' className='service-icon' />
                            </div>
                        ))}
                    </div>
                    <div className='service-list-content'>
                        {SERVICES.map((service) => (
                            <div className='service-item' key={service.no}>
                                <div className='service-text-content'>
                                    <div className='perspective'>
                                        <Reveal
                                            mode='view'
                                            wrapper={false}
                                            innerClassName='perspective-load1-on-view'
                                        >
                                            <h2 className='service-title'>{service.words[0]}</h2>
                                        </Reveal>
                                        <Reveal
                                            mode='view'
                                            delay={0.1}
                                            wrapper={false}
                                            innerClassName='perspective-load2-on-view'
                                        >
                                            <h2 className='service-title'>{service.words[1]}</h2>
                                        </Reveal>
                                    </div>
                                    <div className='service-no'>{service.no}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className='background-black' />
        </div>
    )
}
